import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { API } from "@/App";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND}${url}` : url);

export default function AdminGallery({ headers }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setPhotos(data);
    } catch {
      toast.error("Impossible de charger la galerie.");
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choisissez d'abord une photo.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await axios.post(`${API}/admin/gallery`, fd, { headers });
      toast.success("Photo ajoutée à la galerie.");
      setCaption("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchPhotos();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'envoi de la photo.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/admin/gallery/${p.id}`, { headers });
      setPhotos((list) => list.filter((x) => x.id !== p.id));
      toast.success("Photo supprimée de la galerie.");
    } catch {
      toast.error("Impossible de supprimer cette photo.");
    }
  };

  return (
    <section className="mt-14" data-testid="admin-gallery-manager">
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Galerie photos</h2>
      <p className="mt-1 text-sm text-stone-500">
        Ajoutez ou supprimez les photos visibles sur la page d'accueil.
      </p>

      <form onSubmit={upload} className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-burgundy/10 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-burgundy hover:file:bg-burgundy/20"
          data-testid="gallery-file-input"
        />
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Légende (facultative)"
          className="min-w-40 flex-1 rounded-xl border border-stone-200 bg-cream/60 px-4 py-2.5 text-sm outline-none transition-all placeholder:text-stone-400 focus:border-burgundy focus:ring-2 focus:ring-burgundy/15"
          data-testid="gallery-caption-input"
        />
        <button
          type="submit"
          disabled={uploading}
          className="flex items-center gap-2 rounded-full bg-burgundy px-5 py-2.5 text-xs font-semibold text-cream transition-all hover:bg-burgundy-dark disabled:opacity-60"
          data-testid="gallery-upload-button"
        >
          <ImagePlus className="h-4 w-4" />
          {uploading ? "Envoi…" : "Ajouter la photo"}
        </button>
      </form>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-gallery-grid">
        {photos.map((p) => (
          <div key={p.id} className="group relative overflow-hidden rounded-xl border border-stone-200 bg-white" data-testid={`admin-gallery-photo-${p.id}`}>
            <img src={photoSrc(p.url)} alt={p.caption || "Photo de la galerie"} className="aspect-square w-full object-cover" loading="lazy" />
            {p.caption && (
              <p className="truncate px-3 py-2 text-xs text-stone-600">{p.caption}</p>
            )}
            <button
              onClick={() => remove(p)}
              aria-label="Supprimer cette photo"
              className="absolute right-2 top-2 rounded-full bg-white/90 p-2 text-red-700 opacity-0 shadow transition-opacity hover:bg-red-50 group-hover:opacity-100"
              data-testid={`delete-gallery-${p.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {photos.length === 0 && (
          <p className="col-span-full rounded-xl border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500" data-testid="admin-gallery-empty">
            Aucune photo pour le moment.
          </p>
        )}
      </div>
    </section>
  );
}
